/**
 * JV Testimonials — carousel (scroll-snap).
 * Setas e autoplay: 1 card por vez, com loop no fim.
 * Zero dependências.
 */
class JVTestimonials extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[ref="track"]');
    this.viewport = this.querySelector('[ref="viewport"]');
    this.slides = Array.from(this.querySelectorAll('[data-jv-t-slide]'));
    this.prevBtns = Array.from(this.querySelectorAll('[data-jv-t-prev]'));
    this.nextBtns = Array.from(this.querySelectorAll('[data-jv-t-next]'));
    this.autoplay = this.dataset.autoplay === 'true';
    this.autoplayMs = Math.max(1000, Number(this.dataset.autoplayMs) || 3000);
    this.slideIndex = 0;
    this.autoplayTimer = null;
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!this.track || this.slides.length === 0) return;

    this.onScroll = () => {
      window.clearTimeout(this.scrollSyncTimer);
      this.scrollSyncTimer = window.setTimeout(() => this.syncFromScroll(), 60);
    };
    this.onPause = () => this.stopAutoplay();
    this.onResume = () => {
      if (this.autoplay && !this.reduceMotion) this.startAutoplay();
    };
    this.onResize = () => {
      this.syncFromScroll();
    };

    this.bind();
    this.syncFromScroll();
    if (this.autoplay && !this.reduceMotion) this.startAutoplay();
  }

  disconnectedCallback() {
    this.stopAutoplay();
    this.viewport?.removeEventListener('scroll', this.onScroll);
    this.removeEventListener('mouseenter', this.onPause);
    this.removeEventListener('mouseleave', this.onResume);
    this.removeEventListener('focusin', this.onPause);
    this.removeEventListener('focusout', this.onResume);
    window.removeEventListener('resize', this.onResize);
  }

  bind() {
    this.viewport?.addEventListener('scroll', this.onScroll, { passive: true });
    this.addEventListener('mouseenter', this.onPause);
    this.addEventListener('mouseleave', this.onResume);
    this.addEventListener('focusin', this.onPause);
    this.addEventListener('focusout', this.onResume);
    window.addEventListener('resize', this.onResize, { passive: true });

    for (const btn of this.prevBtns) {
      btn.addEventListener('click', () => {
        this.goPrev();
        this.resetAutoplay();
      });
    }

    for (const btn of this.nextBtns) {
      btn.addEventListener('click', () => {
        this.goNext();
        this.resetAutoplay();
      });
    }
  }

  get slideCount() {
    return this.slides.length;
  }

  goNext() {
    const max = this.slideCount - 1;
    const next = this.slideIndex >= max ? 0 : this.slideIndex + 1;
    this.goToSlide(next);
  }

  goPrev() {
    const max = this.slideCount - 1;
    const prev = this.slideIndex <= 0 ? max : this.slideIndex - 1;
    this.goToSlide(prev);
  }

  goToSlide(index) {
    const max = this.slideCount - 1;
    if (max < 0 || !this.viewport || !this.track) return;

    const next = Math.max(0, Math.min(max, index));
    const target = this.slides[next];
    if (!target) return;

    const left = target.offsetLeft - this.track.offsetLeft;
    this.viewport.scrollTo({
      left,
      behavior: this.reduceMotion ? 'auto' : 'smooth',
    });
    this.slideIndex = next;
    this.updateChrome();
  }

  syncFromScroll() {
    if (!this.viewport || !this.track || this.slides.length === 0) return;

    const scrollLeft = this.viewport.scrollLeft;
    const maxScroll = Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);

    if (scrollLeft >= maxScroll - 4) {
      this.slideIndex = this.slideCount - 1;
      this.updateChrome();
      return;
    }

    if (scrollLeft <= 4) {
      this.slideIndex = 0;
      this.updateChrome();
      return;
    }

    let closest = 0;
    let closestDist = Infinity;

    for (let i = 0; i < this.slides.length; i++) {
      const left = this.slides[i].offsetLeft - this.track.offsetLeft;
      const dist = Math.abs(scrollLeft - left);
      if (dist < closestDist) {
        closestDist = dist;
        closest = i;
      }
    }

    this.slideIndex = closest;
    this.updateChrome();
  }

  updateChrome() {
    const canLoop = this.slideCount > 1;
    for (const btn of this.prevBtns) btn.disabled = !canLoop;
    for (const btn of this.nextBtns) btn.disabled = !canLoop;
    this.setAttribute('aria-live', this.autoplayTimer ? 'off' : 'polite');
  }

  startAutoplay() {
    this.stopAutoplay();
    if (this.slideCount <= 1) return;

    this.autoplayTimer = window.setTimeout(() => {
      this.goNext();
      this.startAutoplay();
    }, this.autoplayMs);

    this.updateChrome();
  }

  stopAutoplay() {
    if (this.autoplayTimer) {
      clearTimeout(this.autoplayTimer);
      this.autoplayTimer = null;
    }
    this.updateChrome();
  }

  resetAutoplay() {
    this.stopAutoplay();
    if (this.autoplay && !this.reduceMotion) this.startAutoplay();
  }
}

if (!customElements.get('jv-testimonials')) {
  customElements.define('jv-testimonials', JVTestimonials);
}
